import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { quotationsAPI } from '../utils/api';
import { useConfirm } from '../hooks/useConfirm';
import { format } from 'date-fns';
import { 
  FiArrowLeft, FiDownload, FiSend, FiCopy, 
  FiCheck, FiX, FiEye, FiMessageCircle, FiMail, FiEdit, FiFileText, FiSave
} from 'react-icons/fi';
import './QuotationDetail.css';

const QuotationDetail = () => {
  const { confirm } = useConfirm();
  const { id } = useParams();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingPrices, setEditingPrices] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);
  const [priceEdits, setPriceEdits] = useState([]);

  useEffect(() => {
    fetchQuotation();
  }, [id]);

  const fetchQuotation = async () => {
    try {
      setLoading(true);
      const response = await quotationsAPI.getById(id);
      setQuotation(response.data.quotation);
    } catch (error) {
      toast.error('Failed to load quotation');
    } finally {
      setLoading(false);
    }
  };

  // Server allows editing these statuses; others are locked
  const priceEditable = quotation && ['draft', 'sent', 'change_requested'].includes(quotation.status);

  const startEditPrices = () => {
    setPriceEdits((quotation.items || []).map(item => ({
      quantity: String(item.quantity ?? 1),
      unitPrice: String(item.unit_price ?? 0),
      taxRate: String(item.tax_rate ?? 0),
    })));
    setEditingPrices(true);
  };

  const cancelEditPrices = () => {
    setEditingPrices(false);
    setPriceEdits([]);
  };

  const updatePriceEdit = (index, field, value) => {
    setPriceEdits(prev => prev.map((edit, i) => (i === index ? { ...edit, [field]: value } : edit)));
  };

  // Live totals preview while editing (mirrors the server formula:
  // line totals are pre-VAT, VAT is quoted on the undiscounted subtotal,
  // quotation-level discount is applied to the subtotal)
  const editedTotals = () => {
    let subtotal = 0;
    let tax = 0;
    (quotation?.items || []).forEach((item, index) => {
      const qty = parseFloat(priceEdits[index]?.quantity ?? item.quantity) || 0;
      const price = parseFloat(priceEdits[index]?.unitPrice ?? item.unit_price) || 0;
      const rate = parseFloat(priceEdits[index]?.taxRate ?? item.tax_rate) || 0;
      subtotal += qty * price;
      tax += qty * price * (rate / 100);
    });
    const discount = parseFloat(quotation?.discount_amount) || 0;
    return { subtotal, tax, discount, total: subtotal - discount + tax };
  };

  const handleSavePrices = async () => {
    setSavingPrices(true);
    try {
      const items = (quotation.items || []).map((item, index) => ({
        itemId: item.item_id,
        name: item.name,
        sku: item.sku || '',
        category: item.category || '',
        description: item.description || '',
        quantity: parseFloat(priceEdits[index]?.quantity ?? item.quantity) || 0,
        unit: item.unit || 'pcs',
        unitPrice: parseFloat(priceEdits[index]?.unitPrice ?? item.unit_price) || 0,
        taxRate: parseFloat(priceEdits[index]?.taxRate ?? item.tax_rate) || 0,
        discountType: 'percentage',
        discountValue: 0,
      }));

      let paymentTerms = null;
      if (quotation.payment_terms) {
        try {
          paymentTerms = typeof quotation.payment_terms === 'string'
            ? JSON.parse(quotation.payment_terms)
            : quotation.payment_terms;
        } catch (e) {
          paymentTerms = null;
        }
      }

      await quotationsAPI.update(id, {
        items,
        issueDate: (quotation.issue_date || '').slice(0, 10),
        expiryDate: (quotation.expiry_date || '').slice(0, 10),
        notes: quotation.notes || '',
        terms: quotation.terms || '',
        poNumber: quotation.po_number || '',
        discountType: quotation.discount_type || 'percentage',
        discountValue: parseFloat(quotation.discount_value) || 0,
        paymentTerms,
      });
      toast.success('Prices updated successfully');
      setEditingPrices(false);
      setPriceEdits([]);
      fetchQuotation();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update prices');
    } finally {
      setSavingPrices(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: quotation?.currency || 'AED',
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'MMM dd, yyyy');
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'badge-draft',
      sent: 'badge-sent',
      viewed: 'badge-viewed',
      accepted: 'badge-accepted',
      rejected: 'badge-rejected',
      expired: 'badge-expired',
      converted: 'badge-converted',
      converted_to_project: 'badge-converted',
      change_requested: 'badge-viewed',
      change_request: 'badge-viewed',
    };
    return statusClasses[status] || 'badge-draft';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      draft: 'Draft',
      sent: 'Sent',
      viewed: 'Viewed',
      accepted: 'Accepted',
      rejected: 'Rejected',
      expired: 'Expired',
      converted: 'Converted',
      converted_to_project: 'Converted',
      change_requested: 'Change Requested',
      change_request: 'Change Requested',
    };
    return statusLabels[status] || status;
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await quotationsAPI.getPDF(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${quotation.quote_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleSend = async () => {
    try {
      await quotationsAPI.send(id);
      toast.success('Quotation marked as sent');
      fetchQuotation();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send quotation');
    }
  };

  const handleAccept = async () => {
    const confirmed = await confirm({
      title: 'Accept Quotation',
      message: 'Mark this quotation as accepted?',
      type: 'success',
      confirmText: 'Accept',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      await quotationsAPI.accept(id);
      toast.success('Quotation marked as accepted');
      fetchQuotation();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to accept quotation');
    }
  };

  const handleReject = async () => {
    const confirmed = await confirm({
      title: 'Reject Quotation',
      message: 'Mark this quotation as rejected?',
      type: 'warning',
      confirmText: 'Reject',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      await quotationsAPI.reject(id);
      toast.success('Quotation marked as rejected');
      fetchQuotation();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reject quotation');
    }
  };

  const handleConvert = async () => {
    const confirmed = await confirm({
      title: 'Convert to Invoice',
      message: 'Convert this quotation to an invoice?',
      type: 'info',
      confirmText: 'Convert',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      const response = await quotationsAPI.convert(id);
      toast.success('Quotation converted to invoice successfully');
      window.location.href = `/invoices/${response.data.invoiceId}`;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to convert quotation');
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete Quotation',
      message: 'Are you sure you want to delete this quotation? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      await quotationsAPI.delete(id);
      toast.success('Quotation deleted');
      window.location.href = '/quotations';
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete quotation');
    }
  };

  const copyPublicLink = () => {
    const url = `${window.location.origin}/quotation/${quotation.public_token}`;
    navigator.clipboard.writeText(url);
    toast.success('Public link copied to clipboard');
  };

  const sendViaWhatsApp = () => {
    const phone = quotation.client_phone;
    if (!phone) {
      toast.error('Client phone number not available');
      return;
    }
    
    const url = `${window.location.origin}/quotation/${quotation.public_token}`;
    const message = `Dear ${quotation.contact_person},\n\nPlease find your quotation ${quotation.quote_number} for the amount of ${formatCurrency(quotation.total)}.\n\nYou can view your quotation online at:\n${url}\n\nThank you for your business!`;
    
    // Clean phone number (remove spaces, dashes, parentheses)
    const cleanPhone = phone.replace(/[\s()-]/g, '');
    
    // Add country code if not present (default to UAE +971)
    const phoneWithCountryCode = cleanPhone.startsWith('+') ? cleanPhone.substring(1) : 
                                  cleanPhone.startsWith('0') ? '971' + cleanPhone.substring(1) :
                                  cleanPhone.startsWith('971') ? cleanPhone : '971' + cleanPhone;
    
    const whatsappUrl = `https://wa.me/${phoneWithCountryCode}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    toast.success('Opening WhatsApp...');
  };

  const [sendingEmail, setSendingEmail] = useState(false);

  const sendViaEmail = async () => {
    if (!quotation.client_email) {
      toast.error('Client email address not available');
      return;
    }
    
    try {
      setSendingEmail(true);
      await quotationsAPI.sendEmail(id);
      toast.success('Quotation sent successfully via email');
      fetchQuotation();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="page-content">
        <div className="empty-state">
          <h3>Quotation not found</h3>
          <Link to="/quotations" className="btn btn-primary">
            <FiArrowLeft /> Back to Quotations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-content">
          <Link to="/quotations" className="back-link">
            <FiArrowLeft /> Back to Quotations
          </Link>
          <h1>{quotation.quote_number}</h1>
          <span className={`badge ${getStatusBadge(quotation.status)}`}>
            {getStatusLabel(quotation.status)}
          </span>
        </div>
        <div className="page-header-actions">
          {quotation.status === 'draft' && (
            <>
              <Link to={`/quotations/${id}/edit`} className="btn btn-primary">
                <FiEdit /> Edit
              </Link>
              <button className="btn btn-success" onClick={handleSend}>
                <FiSend /> Send
              </button>
            </>
          )}
          {['sent', 'viewed'].includes(quotation.status) && (
            <>
              <button className="btn btn-success" onClick={handleAccept}>
                <FiCheck /> Accept
              </button>
              <button className="btn btn-warning" onClick={handleReject}>
                <FiX /> Reject
              </button>
            </>
          )}
          {quotation.status === 'accepted' && (
            <button className="btn btn-primary" onClick={handleConvert}>
              <FiFileText /> Convert to Invoice
            </button>
          )}
          <button className="btn btn-whatsapp" onClick={sendViaWhatsApp}>
            <FiMessageCircle /> WhatsApp
          </button>
          <button className="btn btn-primary" onClick={sendViaEmail} disabled={sendingEmail}>
            <FiMail /> {sendingEmail ? 'Sending...' : 'Send Email'}
          </button>
          <button className="btn btn-secondary" onClick={handleDownloadPDF}>
            <FiDownload /> Download PDF
          </button>
          <button className="btn btn-secondary" onClick={copyPublicLink}>
            <FiCopy /> Copy Link
          </button>
          {!['converted', 'converted_to_project', 'accepted'].includes(quotation.status) && (
            <button className="btn btn-danger" onClick={handleDelete}>
              <FiX /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="quotation-detail-grid">
        <div className="quotation-main">
          {/* Client Info */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Client</h3>
            </div>
            <div className="card-body">
              <h4>{quotation.company_name}</h4>
              <p>{quotation.contact_person}</p>
              <p className="text-muted">{quotation.client_email}</p>
              {quotation.client_address && (
                <p className="text-muted">{quotation.client_address}</p>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Items</h3>
              {editingPrices ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-sm btn-secondary" onClick={cancelEditPrices} disabled={savingPrices}>
                    <FiX /> Cancel
                  </button>
                  <button className="btn btn-sm btn-primary" onClick={handleSavePrices} disabled={savingPrices}>
                    {savingPrices ? (
                      <>
                        <span className="loading-spinner"></span> Saving...
                      </>
                    ) : (
                      <>
                        <FiSave /> Save Prices
                      </>
                    )}
                  </button>
                </div>
              ) : (
                priceEditable && (
                  <button className="btn btn-sm btn-secondary" onClick={startEditPrices} title="Edit line item prices, quantities and tax">
                    <FiEdit /> Edit Prices
                  </button>
                )
              )}
            </div>
            <div className="card-body">
              {editingPrices && (
                <p className="text-muted" style={{ marginBottom: '12px', fontSize: '0.845rem' }}>
                  Update prices, quantities or tax below, then Save Prices. Totals preview updates instantly.
                </p>
              )}
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      {quotation.display_fields?.sku && <th>SKU</th>}
                      {quotation.display_fields?.category && <th>Category</th>}
                      {quotation.display_fields?.item_name !== false && <th>Item Name</th>}
                      {quotation.display_fields?.description !== false && <th style={{ width: '40%' }}>Description</th>}
                      {/* Edit mode always shows the numeric columns so prices
                          stay editable even when hidden by display fields */}
                      {(editingPrices || quotation.display_fields?.unit_price) && <th style={{ textAlign: 'right' }}>Unit Price</th>}
                      {quotation.display_fields?.unit && <th style={{ textAlign: 'center' }}>Unit</th>}
                      {(editingPrices || quotation.display_fields?.quantity) && <th style={{ textAlign: 'center' }}>Qty</th>}
                      {(editingPrices || quotation.display_fields?.tax) && <th style={{ textAlign: 'right' }}>Tax</th>}
                      <th style={{ textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotation.items.map((item, index) => (
                      <tr key={index}>
                        {quotation.display_fields?.sku && <td>{item.sku || '-'}</td>}
                        {quotation.display_fields?.category && <td>{item.category || '-'}</td>}
                        {quotation.display_fields?.item_name !== false && <td className="font-medium">{item.name}</td>}
                        {quotation.display_fields?.description !== false && <td>{item.description || '-'}</td>}
                        {(editingPrices || quotation.display_fields?.unit_price) && (
                          <td style={{ textAlign: 'right' }}>
                            {editingPrices ? (
                              <input
                                type="number"
                                className="form-control"
                                style={{ textAlign: 'right', minWidth: '90px' }}
                                placeholder="Unit price"
                                value={priceEdits[index]?.unitPrice ?? String(item.unit_price ?? 0)}
                                onChange={(e) => updatePriceEdit(index, 'unitPrice', e.target.value)}
                                min="0"
                                step="0.01"
                              />
                            ) : (
                              formatCurrency(item.unit_price)
                            )}
                          </td>
                        )}
                        {quotation.display_fields?.unit && <td style={{ textAlign: 'center' }}>{item.unit || '-'}</td>}
                        {(editingPrices || quotation.display_fields?.quantity) && (
                          <td style={{ textAlign: 'center' }}>
                            {editingPrices ? (
                              <input
                                type="number"
                                className="form-control"
                                style={{ textAlign: 'center', width: '70px' }}
                                placeholder="Qty"
                                value={priceEdits[index]?.quantity ?? String(item.quantity ?? 1)}
                                onChange={(e) => updatePriceEdit(index, 'quantity', e.target.value)}
                                min="0.01"
                                step="0.01"
                              />
                            ) : (
                              item.quantity
                            )}
                          </td>
                        )}
                        {(editingPrices || quotation.display_fields?.tax) && (
                          <td style={{ textAlign: 'right' }}>
                            {editingPrices ? (
                              <input
                                type="number"
                                className="form-control"
                                style={{ textAlign: 'right', width: '70px' }}
                                placeholder="Tax %"
                                value={priceEdits[index]?.taxRate ?? String(item.tax_rate ?? 0)}
                                onChange={(e) => updatePriceEdit(index, 'taxRate', e.target.value)}
                                min="0"
                                step="0.01"
                              />
                            ) : (
                              `${item.tax_rate || 0}%`
                            )}
                          </td>
                        )}
                        {/* Row amount excludes VAT so rows reconcile with the pre-tax Subtotal */}
                        <td className="font-medium" style={{ textAlign: 'right' }}>
                          {editingPrices
                            ? formatCurrency(
                                (parseFloat(priceEdits[index]?.quantity ?? item.quantity) || 0) *
                                (parseFloat(priceEdits[index]?.unitPrice ?? item.unit_price) || 0)
                              )
                            : formatCurrency(Number(item.total || 0) - Number(item.tax_amount || 0))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="quotation-totals">
                {(() => {
                  const totals = editingPrices ? editedTotals() : null;
                  return (
                    <>
                      {editingPrices && (
                        <div className="total-row" style={{ color: 'var(--warning-color)', fontSize: '0.8rem' }}>
                          <span>Unsaved changes</span>
                          <span>Save Prices to apply</span>
                        </div>
                      )}
                      <div className="total-row">
                        <span>Subtotal</span>
                        <span>{formatCurrency(editingPrices ? totals.subtotal : quotation.subtotal)}</span>
                      </div>
                      {(editingPrices ? totals.discount : quotation.discount_amount) > 0 && (
                        <div className="total-row discount">
                          <span>Discount</span>
                          <span>-{formatCurrency(editingPrices ? totals.discount : quotation.discount_amount)}</span>
                        </div>
                      )}
                      <div className="total-row">
                        <span>Tax</span>
                        <span>{formatCurrency(editingPrices ? totals.tax : quotation.tax_amount)}</span>
                      </div>
                      <div className="total-row grand-total">
                        <span>Total</span>
                        <span>{formatCurrency(editingPrices ? totals.total : quotation.total)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        <div className="quotation-sidebar">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Details</h3>
            </div>
            <div className="card-body">
              <div className="detail-item">
                <span className="detail-label">Quotation Number</span>
                <span className="detail-value">{quotation.quote_number}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Issue Date</span>
                <span className="detail-value">{formatDate(quotation.issue_date)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Expiry Date</span>
                <span className={`detail-value ${new Date(quotation.expiry_date) < new Date() && !['accepted', 'converted', 'converted_to_project'].includes(quotation.status) ? 'text-danger' : ''}`}>
                  {formatDate(quotation.expiry_date)}
                </span>
              </div>
              {quotation.po_number && (
                <div className="detail-item">
                  <span className="detail-label">PO Number</span>
                  <span className="detail-value">{quotation.po_number}</span>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-label">Created By</span>
                <span className="detail-value">{quotation.creator_first_name} {quotation.creator_last_name}</span>
              </div>
            </div>
          </div>

          {quotation.notes && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Notes</h3>
              </div>
              <div className="card-body">
                <p>{quotation.notes}</p>
              </div>
            </div>
          )}

          {quotation.terms && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Terms & Conditions</h3>
              </div>
              <div className="card-body">
                <p className="text-muted">{quotation.terms}</p>
              </div>
            </div>
          )}

          {/* Payment Terms */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Payment Terms</h3>
            </div>
            <div className="card-body">
              {quotation.payment_terms ? (
                (() => {
                  try {
                    const pt = typeof quotation.payment_terms === 'string' 
                      ? JSON.parse(quotation.payment_terms) 
                      : quotation.payment_terms;
                    if (pt.type === 'installments' && pt.installments) {
                      return (
                        <div className="payment-schedule-enhanced">
                          {/* Visual Progress Bar */}
                          <div className="payment-progress-display">
                            <div className="progress-bar-display">
                              {pt.installments.map((inst, index) => (
                                <div
                                  key={index}
                                  className="progress-segment-display"
                                  style={{
                                    width: `${inst.percentage}%`,
                                    backgroundColor: `hsl(${200 + (index * 40)}, 70%, 50%)`
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                          
                          {/* Installment Cards */}
                          <div className="installment-cards-display">
                            {pt.installments.map((inst, index) => (
                              <div key={index} className="installment-card-display">
                                <div className="installment-card-header-display">
                                  <span className="installment-number-display">Payment {index + 1}</span>
                                  <span className="installment-percentage-display">{inst.percentage}%</span>
                                </div>
                                <div className="installment-card-body-display">
                                  <div className="installment-amount-display">
                                    {formatCurrency(quotation.total * (inst.percentage / 100))}
                                  </div>
                                  
                                  {/* Milestone */}
                                  {inst.milestone && (
                                    <div className="installment-milestone-display">
                                      <span className="milestone-badge">{inst.milestone.replace(/_/g, ' ')}</span>
                                    </div>
                                  )}
                                  
                                  {/* Due Date */}
                                  <div className="installment-due-display">
                                    {inst.due_date ? (
                                      <span>Due: {formatDate(inst.due_date)}</span>
                                    ) : (
                                      <span>{inst.due_days === 0 ? 'Upon acceptance' : `${inst.due_days} days after acceptance`}</span>
                                    )}
                                  </div>
                                  
                                  {/* Description */}
                                  {inst.description && (
                                    <div className="installment-description-display">
                                      {inst.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return <p className="text-muted">Full payment upon acceptance</p>;
                  } catch (e) {
                    return <p className="text-muted">Full payment upon acceptance</p>;
                  }
                })()
              ) : (
                <p className="text-muted">Full payment upon acceptance</p>
              )}
            </div>
          </div>

          {/* History */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Activity</h3>
            </div>
            <div className="card-body">
              <div className="activity-list">
                {quotation.history.slice(0, 10).map((item, index) => (
                  <div key={index} className="activity-item">
                    <div className="activity-icon">
                      {item.action === 'created' && <FiFileText />}
                      {item.action === 'sent' && <FiSend />}
                      {item.action === 'viewed' && <FiEye />}
                      {item.action === 'accepted' && <FiCheck />}
                      {item.action === 'rejected' && <FiX />}
                      {item.action === 'converted' && <FiFileText />}
                      {item.action === 'emailed' && <FiMail />}
                    </div>
                    <div className="activity-content">
                      <div className="activity-text">{item.description}</div>
                      <div className="activity-time">{formatDate(item.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationDetail;